import { Button } from '@heroui/react';

import {
  LOG_LEVEL_OPTIONS,
  LOG_TYPE_OPTIONS,
  TIME_OPTIONS
} from '../../constants';
import type { LogLevel, LogType } from '../../types';

import i18n from '~/common/helpers/i18n';
import {
  AsyncAutocomplete,
  type AsyncAutocompleteItem,
  type AsyncAutocompleteSubmitEvent
} from '~/components/AsyncAutocomplete';
import {
  ContentHeader,
  ContentHeaderFilterSelect,
  ContentHeaderInput,
  TimeRangeWithTabs,
  useSearchOnEnter,
  type TimeRange
} from '~/components/ContentHeader';
import { useRetentionDateLimits } from '~/scenes/Monitor/components';

type HeaderBaseProps = {
  pipelineName: string;
  pipelineVersion: string;
  logMessage: string;
  pipelineKey: string;
  level: LogLevel;
  logType: LogType;
  timeRange: TimeRange;
  resetToDefault: boolean;
  retentionValue?: number;
  onPipelineNameChange: (value: string) => void;
  onPipelineVersionChange: (value: string) => void;
  onLogMessageChange: (value: string) => void;
  onPipelineKeyChange: (value: string) => void;
  onLevelChange: (value: LogLevel) => void;
  onLogTypeChange: (value: LogType) => void;
  onTimeRangeChange: (timeRange: TimeRange) => void;
  onSearch: () => void;
  onClear: () => void;
};

type HeaderAutocompleteProps = {
  isAutocompleteEnabled: true;
  pipelineItems: AsyncAutocompleteItem[];
  versionItems: AsyncAutocompleteItem[];
  isPipelineLoading: boolean;
  isVersionLoading: boolean;
  isPipelineError?: boolean;
  isVersionError?: boolean;
  pipelineHasMore: boolean;
  versionHasMore: boolean;
  onPipelineSearch: (query: string) => void;
  onVersionSearch: (query: string) => void;
  onPipelineLoadMore: () => void;
  onVersionLoadMore: () => void;
  onPipelineNameSubmit: (event: AsyncAutocompleteSubmitEvent) => void;
  onVersionSubmit: (event: AsyncAutocompleteSubmitEvent) => void;
};

type HeaderNoAutocompleteProps = {
  isAutocompleteEnabled?: false;
};

type HeaderProps = HeaderBaseProps &
  (HeaderAutocompleteProps | HeaderNoAutocompleteProps);

export function Header(props: HeaderProps) {
  const {
    pipelineName,
    pipelineVersion,
    logMessage,
    pipelineKey,
    level,
    logType,
    timeRange,
    retentionValue,
    onPipelineNameChange,
    onPipelineVersionChange,
    onLogMessageChange,
    onPipelineKeyChange,
    onLevelChange,
    onLogTypeChange,
    onTimeRangeChange,
    onSearch,
    onClear
  } = props;

  const handleKeyDown = useSearchOnEnter(onSearch);
  const { minDate, maxDate } = useRetentionDateLimits(retentionValue);

  return (
    <ContentHeader className='flex-col gap-5 border-b border-outline'>
      <div className='grid flex-1 gap-4 grid-cols-[repeat(auto-fit,minmax(348px,1fr))] w-full'>
        <ContentHeaderInput
          name='logMessage'
          variant='labeled'
          label={
            i18n.t(
              'scenes.pipeline_logs.labels.upper_and_capitalize.capitalized',
              { item: i18n.t('scenes.pipeline_logs.labels.log_message') }
            )!
          }
          placeholder={i18n.t('scenes.pipeline_logs.guidance.log_message')!}
          value={logMessage}
          onChange={onLogMessageChange}
          onKeyDown={handleKeyDown}
          data-testid='monitor-pipeline-logs-input-log-message'
        />

        {props.isAutocompleteEnabled ? (
          <AsyncAutocomplete
            label={
              i18n.t(
                'scenes.pipeline_logs.labels.upper_and_capitalize.capitalized',
                { item: i18n.t('scenes.pipeline_logs.labels.name') }
              )!
            }
            placeholder={i18n.t('scenes.pipeline_logs.guidance.name')!}
            items={props.pipelineItems}
            isLoading={props.isPipelineLoading}
            hasMore={props.pipelineHasMore}
            value={pipelineName || null}
            errorContent={
              props.isPipelineError
                ? i18n.t('scenes.pipeline_logs.messages.autocomplete.error')!
                : undefined
            }
            emptyContent={
              i18n.t('scenes.pipeline_logs.messages.autocomplete.empty')!
            }
            getWildcardItemLabel={term =>
              i18n.t('scenes.pipeline_logs.messages.autocomplete.search_for', {
                term
              })!
            }
            onSearch={props.onPipelineSearch}
            onLoadMore={props.onPipelineLoadMore}
            onSubmit={props.onPipelineNameSubmit}
            onInputChange={onPipelineNameChange}
            data-testid='monitor-pipeline-logs-autocomplete-pipeline-name'
          />
        ) : (
          <ContentHeaderInput
            name='pipelineName'
            variant='labeled'
            label={
              i18n.t(
                'scenes.pipeline_logs.labels.upper_and_capitalize.capitalized',
                { item: i18n.t('scenes.pipeline_logs.labels.name') }
              )!
            }
            placeholder={i18n.t('scenes.pipeline_logs.guidance.name')!}
            value={pipelineName}
            onChange={onPipelineNameChange}
            onKeyDown={handleKeyDown}
            data-testid='monitor-pipeline-logs-input-pipeline-name'
          />
        )}

        {props.isAutocompleteEnabled ? (
          <AsyncAutocomplete
            label={
              i18n.t(
                'scenes.pipeline_logs.labels.upper_and_capitalize.capitalized',
                { item: i18n.t('scenes.pipeline_logs.labels.version') }
              )!
            }
            placeholder={i18n.t('scenes.pipeline_logs.guidance.version')!}
            items={props.versionItems}
            isLoading={props.isVersionLoading}
            hasMore={props.versionHasMore}
            value={pipelineVersion || null}
            errorContent={
              props.isVersionError
                ? i18n.t('scenes.pipeline_logs.messages.autocomplete.error')!
                : undefined
            }
            emptyContent={
              i18n.t('scenes.pipeline_logs.messages.autocomplete.empty')!
            }
            onSearch={props.onVersionSearch}
            onLoadMore={props.onVersionLoadMore}
            onSubmit={props.onVersionSubmit}
            onInputChange={onPipelineVersionChange}
            disableWildcard
            data-testid='monitor-pipeline-logs-autocomplete-pipeline-version'
          />
        ) : (
          <ContentHeaderInput
            name='pipelineVersion'
            variant='labeled'
            label={
              i18n.t(
                'scenes.pipeline_logs.labels.upper_and_capitalize.capitalized',
                { item: i18n.t('scenes.pipeline_logs.labels.version') }
              )!
            }
            placeholder={i18n.t('scenes.pipeline_logs.guidance.version')!}
            value={pipelineVersion}
            onChange={onPipelineVersionChange}
            onKeyDown={handleKeyDown}
            data-testid='monitor-pipeline-logs-input-pipeline-version'
          />
        )}

        <ContentHeaderInput
          name='pipelineKey'
          variant='labeled'
          label={i18n.t('label.pipeline_execution_key')!}
          placeholder={i18n.t('label.enter_pipe_exec_key_assist')!}
          value={pipelineKey}
          onChange={onPipelineKeyChange}
          onKeyDown={handleKeyDown}
          data-testid='monitor-pipeline-logs-input-pipeline-key'
        />
      </div>

      <div className='flex justify-between w-full'>
        <div className='flex items-center gap-1.5'>
          <ContentHeaderFilterSelect
            standalone
            selectedKey={level}
            onSelectionChange={onLevelChange}
            options={LOG_LEVEL_OPTIONS.map(opt => ({
              value: opt.key,
              label: opt.label
            }))}
            ariaLabel='Log level selection'
            data-testid='monitor-pipeline-logs-select-all-log-level'
          />

          <ContentHeaderFilterSelect
            standalone
            selectedKey={logType}
            onSelectionChange={onLogTypeChange}
            options={LOG_TYPE_OPTIONS.map(opt => ({
              value: opt.key,
              label: opt.label
            }))}
            ariaLabel='Log source selection'
            data-testid='monitor-pipeline-logs-select-log-type'
          />

          <div className='ml-5'>
            <TimeRangeWithTabs
              value={timeRange}
              onChange={onTimeRangeChange}
              minDate={minDate}
              maxDate={maxDate}
              timeOptions={TIME_OPTIONS}
              testIdPrefix='monitor-pipeline-logs'
            />
          </div>
        </div>

        <div className='flex items-center gap-1.5'>
          <Button
            color='primary'
            variant='solid'
            onPress={onSearch}
            data-testid='monitor-pipeline-logs-button-search'
            size='sm'
          >
            {i18n.t('common.actions.search')}
          </Button>

          <Button
            color='primary'
            variant='bordered'
            onPress={onClear}
            data-testid='monitor-pipeline-logs-button-clear'
            size='sm'
          >
            {i18n.t('action.clear')}
          </Button>
        </div>
      </div>
    </ContentHeader>
  );
}
